/**
 * Sound Manager using actual sound files
 * Place sound files in /public/sounds/ directory
 */

import { userPreferencesService } from '@/lib/services/user-preferences';
import { supabase } from '@/lib/supabase-singleton';

// Sound category types
export type SoundCategory =
  | 'notifications'
  | 'messages'
  | 'voice'
  | 'calls'
  | 'screenshare'
  | 'social'
  | 'monetization'
  | 'moderation'
  | 'system';

// Sound preferences interface
export interface SoundPreferences {
  master: boolean;
  notifications: boolean;
  messages: boolean;
  voice: boolean;
  calls: boolean;
  screenshare: boolean;
  social: boolean;
  monetization: boolean;
  moderation: boolean;
  system: boolean;
}

// Default sound preferences (all enabled)
const DEFAULT_SOUND_PREFERENCES: SoundPreferences = {
  master: true,
  notifications: true,
  messages: true,
  voice: true,
  calls: true,
  screenshare: true,
  social: true,
  monetization: true,
  moderation: true,
  system: true,
};

class SoundManagerFiles {
  private sounds: Map<string, HTMLAudioElement> = new Map();
  private enabled: boolean = true; // Enabled by default per user request
  private volume: number = 0.5;
  private initialized: boolean = false;
  private soundPreferences: SoundPreferences = { ...DEFAULT_SOUND_PREFERENCES };
  private soundPreferencesLoaded: boolean = false;

  // Sound file paths (relative to /public) - supports both .mp3 and .wav
  private soundPaths: Record<string, string> = {
    // Notifications
    notification: '/sounds/notification',
    mention: '/sounds/mention',
    dm: '/sounds/dm',

    // Messages
    message: '/sounds/message',
    reaction: '/sounds/reaction',
    typing: '/sounds/typing',

    // Voice
    userJoin: '/sounds/user-join',
    userLeave: '/sounds/user-leave',
    mute: '/sounds/mute',
    unmute: '/sounds/unmute',
    deafen: '/sounds/deafen',
    undeafen: '/sounds/undeafen',

    // Calls
    callRing: '/sounds/call-ring',
    callConnect: '/sounds/call-connect',
    callEnd: '/sounds/call-end',
    hangupCall: '/sounds/hangupcall',
    calling: '/sounds/calling',

    // Screen Share
    screenShareStart: '/sounds/screen-share-start',
    screenShareStop: '/sounds/screen-share-stop',

    // Social
    friendRequest: '/sounds/friend-request',
    inviteAccepted: '/sounds/invite-accepted',
    joinSpace: '/sounds/join-space',

    // Monetization
    giftReceived: '/sounds/gift-received',
    tipReceived: '/sounds/tip-received',
    streamStart: '/sounds/stream-start',

    // Moderation
    kick: '/sounds/kick',
    ban: '/sounds/ban',

    // System
    success: '/sounds/success',
    error: '/sounds/error',
  };

  // Map each sound to its category
  private soundCategoryMap: Record<string, SoundCategory> = {
    // Notifications category
    notification: 'notifications',
    mention: 'notifications',
    dm: 'notifications',

    // Messages category
    message: 'messages',
    reaction: 'messages',
    typing: 'messages',

    // Voice category
    userJoin: 'voice',
    userLeave: 'voice',
    mute: 'voice',
    unmute: 'voice',
    deafen: 'voice',
    undeafen: 'voice',

    // Calls category
    callRing: 'calls',
    callConnect: 'calls',
    callEnd: 'calls',
    hangupCall: 'calls',
    calling: 'calls',

    // Screen Share category
    screenShareStart: 'screenshare',
    screenShareStop: 'screenshare',

    // Social category
    friendRequest: 'social',
    inviteAccepted: 'social',
    joinSpace: 'social',

    // Monetization category
    giftReceived: 'monetization',
    tipReceived: 'monetization',
    streamStart: 'monetization',

    // Moderation category
    kick: 'moderation',
    ban: 'moderation',

    // System category
    success: 'system',
    error: 'system',
  };

  // Fallback to generated sounds if files don't exist
  private fallbackSounds: Map<string, string> = new Map();

  constructor() {
    if (typeof window !== 'undefined') {
      this.initializeWithPreferences();
      this.loadSoundPreferences();
      // DO NOT preload sounds here - wait for user interaction to avoid AudioContext errors
    }
  }

  /**
   * Lazy initialize sounds on first play (user interaction required)
   */
  private async ensureInitialized(): Promise<void> {
    if (this.initialized || typeof window === 'undefined') return;
    await this.preloadSounds();
  }

  /**
   * Initialize with user preferences from database
   */
  private async initializeWithPreferences(): Promise<void> {
    try {
      const prefs = await userPreferencesService.loadPreferences();
      this.enabled = prefs.sound_enabled;
      this.volume = prefs.sound_volume;
      
      // Subscribe to preference changes
      userPreferencesService.subscribe((newPrefs) => {
        this.enabled = newPrefs.sound_enabled;
        this.volume = newPrefs.sound_volume;
        this.updateAllVolumes();
      });
    } catch (error) {
      // Fallback to localStorage
      this.loadSettings();
    }
  }

  /**
   * Load sound preferences from Supabase profiles table
   * Fetches sound_preferences column and caches locally
   */
  private async loadSoundPreferences(): Promise<void> {
    try {
      // First try to load from localStorage cache
      const cached = this.loadSoundPreferencesFromCache();
      if (cached) {
        this.soundPreferences = cached;
        this.soundPreferencesLoaded = true;
      }

      // Then fetch from Supabase
      const { data: { user } } = await supabase.auth.getUser();

      if (user) {
        const { data, error } = await supabase
          .from('profiles')
          .select('sound_preferences')
          .eq('id', user.id)
          .single();

        if (data && !error && data.sound_preferences) {
          // Merge with defaults to ensure all categories exist
          this.soundPreferences = {
            ...DEFAULT_SOUND_PREFERENCES,
            ...data.sound_preferences,
          };

          // Cache locally
          this.cacheSoundPreferences(this.soundPreferences);
          this.soundPreferencesLoaded = true;
        }
      }
    } catch (error) {
      console.error('[SoundManager] Failed to load sound preferences:', error);
      // Keep using cached or default preferences
    }
  }

  /**
   * Cache sound preferences to localStorage
   */
  private cacheSoundPreferences(prefs: SoundPreferences): void {
    if (typeof window === 'undefined' || !window.localStorage) return;

    try {
      localStorage.setItem('gjyl-sound-preferences', JSON.stringify(prefs));
    } catch (e) {
      console.error('[SoundManager] Failed to cache sound preferences:', e);
    }
  }

  /**
   * Load sound preferences from localStorage cache
   */
  private loadSoundPreferencesFromCache(): SoundPreferences | null {
    if (typeof window === 'undefined' || !window.localStorage) return null;

    try {
      const cached = localStorage.getItem('gjyl-sound-preferences');
      if (cached) {
        return JSON.parse(cached);
      }
    } catch (e) {
      console.error('[SoundManager] Failed to load cached sound preferences:', e);
    }
    return null;
  }

  /**
   * Check if a sound's category is enabled
   */
  private isCategoryEnabled(soundName: string): boolean {
    const category = this.soundCategoryMap[soundName];
    if (!category) {
      // If sound has no category mapping, allow it
      return true;
    }
    return this.soundPreferences[category] ?? true;
  }

  /**
   * Update a category's enabled state
   */
  async setCategoryEnabled(category: SoundCategory, enabled: boolean): Promise<void> {
    this.soundPreferences[category] = enabled;
    this.cacheSoundPreferences(this.soundPreferences);

    // Sync to Supabase
    try {
      const { data: { user } } = await supabase.auth.getUser();

      if (user) {
        await supabase
          .from('profiles')
          .update({
            sound_preferences: this.soundPreferences,
            updated_at: new Date().toISOString()
          })
          .eq('id', user.id);
      }
    } catch (error) {
      console.error('[SoundManager] Failed to sync sound preferences:', error);
    }
  }

  /**
   * Set master enabled state
   * When master is OFF, all sounds are disabled globally
   * Individual category preferences are preserved
   */
  async setMasterEnabled(enabled: boolean): Promise<void> {
    this.soundPreferences.master = enabled;
    this.cacheSoundPreferences(this.soundPreferences);

    // Sync to Supabase
    try {
      const { data: { user } } = await supabase.auth.getUser();

      if (user) {
        await supabase
          .from('profiles')
          .update({
            sound_preferences: this.soundPreferences,
            updated_at: new Date().toISOString()
          })
          .eq('id', user.id);
      }
    } catch (error) {
      console.error('[SoundManager] Failed to sync master enabled state:', error);
    }
  }

  /**
   * Check if master is enabled
   */
  isMasterEnabled(): boolean {
    return this.soundPreferences.master;
  }

  /**
   * Get current sound preferences
   */
  getSoundPreferences(): SoundPreferences {
    return { ...this.soundPreferences };
  }

  /**
   * Reload sound preferences from server
   */
  async refreshSoundPreferences(): Promise<void> {
    await this.loadSoundPreferences();
  }

  /**
   * Update volume for all loaded sounds
   */
  private updateAllVolumes(): void {
    this.sounds.forEach(audio => {
      audio.volume = this.volume;
    });
  }

  /**
   * Load user settings from localStorage
   */
  private loadSettings(): void {
    if (typeof window === 'undefined' || !window.localStorage) return;
    
    const settings = localStorage.getItem('gjyl-sound-settings');
    if (settings) {
      try {
        const parsed = JSON.parse(settings);
        this.enabled = parsed.enabled ?? true; // Default to enabled
        this.volume = parsed.volume ?? 0.5;
      } catch (e) {
        console.error('[SoundManager] Failed to load settings:', e);
      }
    }
    // If no settings saved, keep default (disabled)
  }

  /**
   * Save settings to localStorage
   */
  private saveSettings(): void {
    if (typeof window === 'undefined' || !window.localStorage) return;
    
    localStorage.setItem('gjyl-sound-settings', JSON.stringify({
      enabled: this.enabled,
      volume: this.volume
    }));
  }

  /**
   * Preload all sound files
   */
  private async preloadSounds(): Promise<void> {
    if (typeof window === 'undefined') return;

    // Preloading sound files silently
    
    for (const [key, basePath] of Object.entries(this.soundPaths)) {
      let loaded = false;
      
      // Try different file extensions in order of preference
      const extensions = ['.wav', '.mp3', '.ogg', '.m4a'];
      
      for (const ext of extensions) {
        if (loaded) break;
        
        const path = basePath + ext;
        try {
          const audio = new Audio(path);
          audio.volume = this.volume;
          
          // Test if file exists by trying to load it
          await new Promise((resolve, reject) => {
            audio.addEventListener('canplaythrough', resolve, { once: true });
            audio.addEventListener('error', reject, { once: true });
            audio.load();
          });
          
          this.sounds.set(key, audio);
          // Successfully loaded sound file
          loaded = true;
        } catch (error) {
          // Try next extension
          continue;
        }
      }
      
      if (!loaded) {
        // Could not load sound file, using fallback
        // Create fallback sound
        this.createFallbackSound(key);
      }
    }
    
    this.initialized = true;
    // Sound system initialized
  }

  /**
   * Create a simple fallback sound if file doesn't exist
   */
  private createFallbackSound(soundName: string): void {
    if (typeof window === 'undefined') return;
    
    try {
      const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      
      // Different frequencies for different sounds
      const frequencies: Record<string, number> = {
        // Notifications
        notification: 800,
        mention: 850,
        dm: 780,

        // Messages
        message: 600,
        reaction: 550,
        typing: 400,

        // Voice
        userJoin: 620,
        userLeave: 520,
        mute: 380,
        unmute: 450,
        deafen: 320,
        undeafen: 400,

        // Calls
        callRing: 440,
        callConnect: 659,
        callEnd: 330,
        hangupCall: 300,
        calling: 480,

        // Screen Share
        screenShareStart: 700,
        screenShareStop: 580,

        // Social
        friendRequest: 700,
        inviteAccepted: 750,
        joinSpace: 680,

        // Monetization
        giftReceived: 880,
        tipReceived: 920,
        streamStart: 800,

        // Moderation
        kick: 280,
        ban: 240,

        // System
        success: 523,
        error: 350,
      };
      
      oscillator.frequency.value = frequencies[soundName] || 500;
      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);
      
      // Create a short beep
      gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.2);
      
      oscillator.start(audioContext.currentTime);
      oscillator.stop(audioContext.currentTime + 0.2);
      
      // Create a data URL for the sound
      const audio = new Audio();
      audio.volume = this.volume;
      
      // Store the fallback
      this.sounds.set(soundName, audio);
      // Created fallback sound
    } catch (e) {
      console.error(`[SoundManager] Could not create fallback for ${soundName}:`, e);
    }
  }

  /**
   * Play a sound by name
   * Checks global enabled state, master preference, and category-specific preferences
   */
  async play(soundName: string): Promise<void> {
    if (!this.enabled || typeof window === 'undefined') return;

    // Check if master is enabled in preferences
    if (!this.soundPreferences.master) {
      return;
    }

    // Check if this sound's category is enabled
    if (!this.isCategoryEnabled(soundName)) {
      return;
    }

    // Lazy initialize on first play (user interaction required)
    await this.ensureInitialized();

    const audio = this.sounds.get(soundName);
    if (audio) {
      try {
        audio.currentTime = 0;
        audio.volume = this.volume;
        await audio.play();
      } catch (e) {
        // Failed to play sound (likely user hasn't interacted with page yet)
      }
    } else {
      // Sound not found
    }
  }

  /**
   * Play a sound by name, bypassing all preference checks
   * Use this for UI feedback sounds (test playback, toggle confirmations)
   * that should play regardless of user preferences
   */
  async playForce(soundName: string): Promise<void> {
    if (typeof window === 'undefined') return;

    // Lazy initialize on first play (user interaction required)
    await this.ensureInitialized();

    const audio = this.sounds.get(soundName);
    if (audio) {
      try {
        audio.currentTime = 0;
        audio.volume = this.volume;
        await audio.play();
      } catch (e) {
        // Failed to play sound (likely user hasn't interacted with page yet)
      }
    }
  }

  /**
   * Wait for sounds to be loaded
   */
  private async waitForInitialization(): Promise<void> {
    let attempts = 0;
    while (!this.initialized && attempts < 50) {
      await new Promise(resolve => setTimeout(resolve, 100));
      attempts++;
    }
  }

  /**
   * Download sound file instructions
   */
  getDownloadInstructions(): string {
    return `
    To add custom sounds, download sound files and place them in:
    /public/sounds/
    
    Required files:
    ${Object.entries(this.soundPaths).map(([key, path]) => 
      `- ${path.split('/').pop()} (${key})`
    ).join('\n    ')}
    
    Recommended sources:
    - https://freesound.org
    - https://www.zapsplat.com
    - https://opengameart.org
    
    File format: MP3 or OGG, under 100KB preferred
    `;
  }

  /**
   * Test if a sound file exists
   */
  async testSound(soundName: string): Promise<boolean> {
    const audio = this.sounds.get(soundName);
    return !!audio;
  }

  /**
   * List all available sounds
   */
  getAvailableSounds(): string[] {
    return Array.from(this.sounds.keys());
  }

  /**
   * Convenience methods for common sounds
   */
  async playNotification(): Promise<void> {
    await this.play('notification');
  }

  async playMessage(): Promise<void> {
    await this.play('message');
  }

  async playSuccess(): Promise<void> {
    await this.play('success');
  }

  async playError(): Promise<void> {
    await this.play('error');
  }

  async playFriendRequest(): Promise<void> {
    await this.play('friendRequest');
  }

  async playCallRing(): Promise<void> {
    await this.play('callRing');
  }

  async playCallConnect(): Promise<void> {
    await this.play('callConnect');
  }

  async playCallEnd(): Promise<void> {
    await this.play('callEnd');
  }

  async playHangupCall(): Promise<void> {
    await this.play('hangupCall');
  }

  async playCalling(): Promise<void> {
    await this.play('calling');
  }

  async playTyping(): Promise<void> {
    await this.play('typing');
  }

  // Notification sounds
  async playMention(): Promise<void> {
    await this.play('mention');
  }

  async playDm(): Promise<void> {
    await this.play('dm');
  }

  // Message sounds
  async playReaction(): Promise<void> {
    await this.play('reaction');
  }

  // Voice sounds
  async playUserJoin(): Promise<void> {
    await this.play('userJoin');
  }

  async playUserLeave(): Promise<void> {
    await this.play('userLeave');
  }

  async playMute(): Promise<void> {
    await this.play('mute');
  }

  async playUnmute(): Promise<void> {
    await this.play('unmute');
  }

  async playDeafen(): Promise<void> {
    await this.play('deafen');
  }

  async playUndeafen(): Promise<void> {
    await this.play('undeafen');
  }

  // Screen Share sounds
  async playScreenShareStart(): Promise<void> {
    await this.play('screenShareStart');
  }

  async playScreenShareStop(): Promise<void> {
    await this.play('screenShareStop');
  }

  // Social sounds
  async playInviteAccepted(): Promise<void> {
    await this.play('inviteAccepted');
  }

  async playJoinSpace(): Promise<void> {
    await this.play('joinSpace');
  }

  // Monetization sounds
  async playGiftReceived(): Promise<void> {
    await this.play('giftReceived');
  }

  async playTipReceived(): Promise<void> {
    await this.play('tipReceived');
  }

  async playStreamStart(): Promise<void> {
    await this.play('streamStart');
  }

  // Moderation sounds
  async playKick(): Promise<void> {
    await this.play('kick');
  }

  async playBan(): Promise<void> {
    await this.play('ban');
  }

  /**
   * Set volume (0-1)
   */
  async setVolume(volume: number): Promise<void> {
    this.volume = Math.max(0, Math.min(1, volume));
    
    // Update all loaded sounds
    this.sounds.forEach(audio => {
      audio.volume = this.volume;
    });
    
    // Save to database and localStorage
    await userPreferencesService.updatePreference('sound_volume', this.volume);
    this.saveSettings();
  }

  /**
   * Toggle sounds on/off
   */
  async setEnabled(enabled: boolean): Promise<void> {
    this.enabled = enabled;
    
    // Save to database and localStorage
    await userPreferencesService.updatePreference('sound_enabled', enabled);
    this.saveSettings();
  }

  /**
   * Check if sounds are enabled
   */
  isEnabled(): boolean {
    return this.enabled;
  }

  /**
   * Get current volume
   */
  getVolume(): number {
    return this.volume;
  }

  /**
   * Reload sounds (useful after adding new files)
   */
  async reloadSounds(): Promise<void> {
    this.sounds.clear();
    this.initialized = false;
    await this.preloadSounds();
  }
}

// Export singleton instance
export const soundManagerFiles = new SoundManagerFiles();